/**
 * Turning a failed email/password sign-in into "you registered with Google".
 *
 * Users who signed up with Apple or Google forget they did, try the email/password form that was
 * added later, fail, then try a password reset and fail again because there is no password to
 * reset. Both forms consult `SocialSigninService.getSignInProviders()` and use the helpers here to
 * decide whether there is anything more specific to say than "incorrect email or password".
 */

/** Firebase's provider id for an email/password credential. */
export const PASSWORD_PROVIDER = 'password';

// What Firebase returns for a sign-in that failed on the credential rather than on, say, a
// disabled account or a network fault. Which one you get depends on whether email-enumeration
// protection is enabled on the project, so all of them have to be treated the same. The last is
// the pre-v10 SDK spelling, kept because older app builds are still in the wild.
const CREDENTIAL_ERROR_CODES = ['auth/user-not-found', 'auth/wrong-password', 'auth/invalid-credential', 'auth/invalid-login-credentials'];

/**
 * Whether a Firebase error code means "those credentials did not work", and so is worth spending
 * a provider lookup on. Anything else — a disabled account, a network failure, too many attempts —
 * keeps its own message.
 */
export const isCredentialError = (code: string): boolean => CREDENTIAL_ERROR_CODES.includes(code);

// Only the providers the apps actually offer. An id that is not here contributes nothing, so a
// provider added to Firebase but not to the login page cannot produce a message naming a button
// that does not exist.
const PROVIDER_NAMES: { [providerId: string]: string } = {
    'google.com': 'Google',
    'apple.com': 'Apple',
    'facebook.com': 'Facebook'
};

/**
 * The provider name(s) to show, or null when there is nothing useful to say.
 *
 * Null when the lookup told us nothing, and — importantly — null when the account *does* have a
 * password, because then the sign-in really did fail on a wrong password and claiming otherwise
 * would send the user to a button that will not help.
 *
 * Joined with commas rather than "or": the result is interpolated into a translated string, and
 * an English conjunction baked in here would not survive translation.
 */
export const describeOtherProviders = (providers: string[]): string | null => {
    if (!providers?.length || providers.includes(PASSWORD_PROVIDER)) {
        return null;
    }

    const names = providers.map((providerId) => PROVIDER_NAMES[providerId]).filter((name) => !!name);

    return names.length ? names.join(', ') : null;
};
